import { min, max } from 'd3-array';
import { markerType, ohlcType, shapeType } from '../enums/indicators/price';
import { types as eventTypes } from '../enums/event';
import LastCloseFlag from '../flags/LastCloseFlag';
import Indicator from '../Indicator';
import BarMarker from '../markers/BarMarker';
import CandlestickMarker from '../markers/CandlestickMarker';
import CircleMarker from '../markers/CircleMarker';
import DiamondMarker from '../markers/DiamondMarker';
import LineMarker from '../markers/LineMarker';
import SquareMarker from '../markers/SquareMarker';
import TriangleMarker from '../markers/TriangleMarker';
import subpixel from '../util/canvasSubpixelOffset';

/** Price indicator */
export default class PriceIndicator extends Indicator {
	/** @param {object} args Custom args */
	constructor(args) {
		super(args);
		/** @type {object} */
		this.flags['lastClose'] = new LastCloseFlag({
			panel: this.panel,
			indicator: this,
			id: 'lastClose'
		});
		this.state = { hover: false };
		this.hoverEventOver = eventTypes.INDICATOR_MOUSE_OVER;
		this.hoverEventOut = eventTypes.INDICATOR_MOUSE_OUT;
		this.hoverEventMove = eventTypes.INDICATOR_MOUSE_MOVE;
	}

	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {object[]} inputs - Indicator inputs
	 * @property {string} dataSeries - Which data series to use
	 * @property {string} markerType - Indicator marker type - 'posneg' |
	 * 'candlestick' | 'fill' | 'ohlc' | 'hlc' | 'bar' | 'line'
	 * @property {string} candleFillType - Whether to fill candlestick markers -
	 * 'hollow' | 'filled'
	 * @property {string} candleFillRule - How to fill candlestick markers -
	 * 'default' | 'openClose'
	 * @property {boolean} ohlcPosNeg - Set false for OHLC/HLC to get monochrome coloring
	 * @property {string} symbol - Symbol for this indicator
	 */
	_getDefaultParams() {
		return {
			id: 'price',
			name: 'Price',
			description: 'The Price Indicator represents the price action of a security in various visual formats such as Line, Fill, OHLC (Open, High, Low, Close), and Candlestick.',
			inputs: [{ name: 'type', value: 'ohlc' }],
			dataSeries: 'close',
			markerType: markerType.LINE, // posneg, candlestick, fill, ohlc, hlc, bar
			candleFillType: 'hollow', // "hollow", "filled"
			candleFillRule: 'default', // "default", "openClose"
			ohlcPosNeg: true, // for OHLC/HLC set false for monochrome coloring,
			ohlcType: ohlcType.POSNEG,
			symbol: this.core.params.symbol
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		const dataset =
			typeof data.price === 'object' && data.price.length
				? data.price
				: data.length ? data : [];
		let minVal;
		switch (this.params.markerType) {
			case markerType.CANDLESTICK:
			case markerType.HLC:
			case markerType.OHLC:
				// use low if available, otherwise use close or value (used in custom datasets)
				minVal = min(dataset, i => i.low || i.close || i.value); break;
			default: // bar, fill, line, linewithshapes, posneg, shape, stepped
				// use close if available, otherwise use value (used in custom datasets)
				minVal = min(dataset, i => i.close || i.value); break;
		}

		// Add normalized point (if available) to make sure line to normalized point doesn't draw through top/bottom of chart
		const normalizedPoint = dataset.length ? this._getNormalizedPoint(dataset[0].dateIndex) : null;
		if (normalizedPoint) minVal = Math.min(minVal, normalizedPoint[1]);

		return minVal;
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		const dataset =
			typeof data.price === 'object' && data.price.length
				? data.price
				: data.length ? data : [];
		let maxVal;
		switch (this.params.markerType) {
			case markerType.CANDLESTICK:
			case markerType.HLC:
			case markerType.OHLC:
				// use high if available, otherwise use close or value (used in custom datasets)
				maxVal = max(dataset, i => i.high || i.close || i.value); break;
			default: // bar, fill, line, linewithshapes, posneg, shape, stepped
				// use close if available, otherwise use value (used in custom datasets)
				maxVal = max(dataset, i => i.close || i.value); break;
		}

		// Add normalized point (if available) to make sure line to normalized point doesn't draw through top/bottom of chart
		const normalizedPoint = dataset.length ? this._getNormalizedPoint(dataset[0].dateIndex) : null;
		if (normalizedPoint) maxVal = Math.max(maxVal, normalizedPoint[1]);

		return maxVal;
	}

	/**
	 * @return {object}
	 * @property {BarMarker} bar - Bar marker
	 * @property {CandlestickMarker} candlestick - Candlestick marker
	 * @property {CircleMarker} circle - Circle marker
	 * @property {LineMarker} line - Line marker
	 * @property {LineMarker} stepped - Stepped marker
	 */
	_getMarkers() {
		return {
			bar: new BarMarker(),
			candlestick: new CandlestickMarker(),
			line: new LineMarker(),
			shape: {
				[shapeType.CIRCLE]: new CircleMarker(),
				[shapeType.DIAMOND]: new DiamondMarker(),
				[shapeType.SQUARE]: new SquareMarker(),
				[shapeType.TRIANGLE]: new TriangleMarker()
			},
			stepped: new LineMarker()
		};
	}

	/**
	 * @param {object} data - Indicator data
	 * @return {number[][]}
	 */
	_getDataMap(data = []) {
		if (!data) return [];
		return data.map(d => {
			const close = typeof d.close === 'number' ? d.close : d.value;

			return [
				d.dateIndex,
				close,
				typeof d.open === 'number' ? d.open : close,
				typeof d.high === 'number' ? d.high : close,
				typeof d.low === 'number' ? d.low : close,
				d.diff,
				d.hollow
			];
		});
	}

	// Function to calculate distance from a point to a line segment
	_pointToLineDistance(x, y, x1, y1, x2, y2) {
		// Calculate the distance
		const A = x - x1;
		const B = y - y1;
		const C = x2 - x1;
		const D = y2 - y1;

		const dot = A * C + B * D;
		const lenSQ = C * C + D * D;
		let param = -1;
		if (lenSQ !== 0) { // in case of 0 length line
			param = dot / lenSQ;
		}

		let xx; let
			yy;

		if (param < 0) {
			xx = x1;
			yy = y1;
		} else if (param > 1) {
			xx = x2;
			yy = y2;
		} else {
			xx = x1 + param * C;
			yy = y1 + param * D;
		}

		const dx = x - xx;
		const dy = y - yy;
		return Math.sqrt(dx * dx + dy * dy);
	}

	/**
	 * @param {array} mouse - Mouse coordinates with respect to this indicator's panel
	 * @param {array[]} lineCoords - Array of coordinate arrays [[x,y],[x,y]].  Only used for testing.
	 * @return {boolean}
	 */
	isWithin(mouse, lineCoords) {
		// Adjust for top margin
		const adjustedMouseY = mouse[1] - this.panel.params.style.margin.top;

		// Get line coordinates
		if (!lineCoords) lineCoords = this.markers.line.getLineCoords(this.state.dataPrice, this.xScale, this.yScale, this.params.markerType === 'stepped');
		const threshold = 5; // Distance in pixels to consider the mouse as 'hovering' over the line

		// Check if mouse is near any line segment
		for (let i = 0; i < lineCoords.length - 1; i++) {
			const [x1, y1] = lineCoords[i];
			const [x2, y2] = lineCoords[i + 1];

			if (this._pointToLineDistance(mouse[0], adjustedMouseY, x1, y1, x2, y2) < threshold) {
				return true; // Mouse is within threshold distance of a line segment
			}
		}

		return false; // Mouse is not near any line segment
	}

	/**
	 * Prepends a point to the beginning of the data array at the normalized
	 * date/value position.  This is non-destructive to the overall chart dataset
	 * since the prepend only affects the temporary render dataset.
	 * @param {object} data - Processed indicator data array
	 * @return {object}
	 */
	_prependNormalizedPoint(data) {
		const normalizedPoint = data.length && this._getNormalizedPoint(data[0][0]);
		if (normalizedPoint) data = [normalizedPoint, ...data];

		return data;
	}

	/**
	 * If enabled and conditions are correct, returns the normalized point that is ready for prepending.
	 * Description of correct conditions:
	 * - The chart has not been panned
	 * - The normalizedValues collection must exist
	 * - The params.prependNormalizedPoint feature is enabled
	 * - The chart is interday OR not a mix of domestic and international exchanges
	 * @param {object} data - Processed indicator data array
	 * @return {object}
	 */
	_getNormalizedPoint(firstSeriesIndex) {
		const { core } = this;
		if (
			!core.params.endOffsetDays &&
			core.params.normalizeValues &&
			core.params.prependNormalizedPoint &&
			(
				!core.state.isIntraday ||
				!this._isMixedDomesticIntlExchanges()
			) &&
			(
				this.params.markerType === markerType.LINE ||
				this.params.markerType === markerType.LINEWITHSHAPES ||
				this.params.markerType === markerType.FILL ||
				this.params.markerType === markerType.POSNEG ||
				this.params.markerType === markerType.STEPPED
			)
		) {
			const normalizeDate = new Date(core.params.normalizeValues.Date);
			normalizeDate.setMinutes(normalizeDate.getMinutes() + core.xref[core.params.symbol].utcOffset);
			const normalizedIndex = core.closestExchangeIndex(normalizeDate);

			// Only apply if we haven't panned and normalized point is on screen
			const domainLeft = this.panel.xAxis.primaryScale.domain()[0];
			if (
				normalizedIndex < firstSeriesIndex &&
				normalizedIndex >= domainLeft
			) {
				const normalizedClose = !core.panels[0].isNormalized
					? core.params.normalizeValues.Close
					: 0;

				return [normalizedIndex, normalizedClose];
			}
		}
	}

	/**
	 * Returns true if there is a mix of international and domestic exchanges on the chart
	 * @return {boolean}
	 */
	_isMixedDomesticIntlExchanges() {
		const { core } = this;
		const domesticRE = /^(DJI|MXP|NAQ|NAS|NSQ|NYQ|PSE|TOR)$/;
		const mixedExchanges = {};
		Object.keys(core.data).forEach(xid => {
			mixedExchanges[domesticRE.test(core.xref[xid].exchangeId)] = 1;
		});
		return Object.keys(mixedExchanges).length > 1;
	}

	/**
	 * @param {object} data Symbol data object
	 * @param {string} sessionType Indicator session type
	 */
	render(data = {}, sessionType = 'NormalSession') {
		const ctx = this.panel.rootContext;
		const { core } = this;
		data = data || {};

		const dataset =
			typeof data?.price === 'object' && data.price.length
				? data.price
				: data?.length ? data : [];

		const dataPrice = this._getDataMap(dataset);
		this.state.dataPrice = dataPrice; // store off for isWithin (hover) check later
		let priceMarkerType = this.params.markerType;
		let normalizeValue = 0;
		const hasPreviousClose = data?.previousclose?.length;
		const { style } = this.params;

		// determine normalizeValue for use in posneg markerType
		if (hasPreviousClose) {
			normalizeValue = data.previousclose[data.previousclose.length - 1].previousclose;
		} else if (!core.params.dataNormalized && core.params.normalizeValues) {
			// the dataset isn't normalized, but we know the normalized value (starting value) since it's returned with every API call
			normalizeValue = core.params.normalizeValues.Close;
		}

		// CH-1894 When returnPreviousPoint is enabled, and it's a bar-type marker (markers that actually have a width, like candlesticks), don't draw the first point because it will be truncated otherwise
		if (
			core.params.returnPreviousPoint &&
			typeof data.price === 'object' &&
			data.price.length > 1 &&
			(
				priceMarkerType === markerType.BAR ||
				priceMarkerType === markerType.CANDLESTICK ||
				priceMarkerType === markerType.HLC ||
				priceMarkerType === markerType.OHLC
			)
		) {
			dataPrice.shift();
		}

		// Downgrade posneg markerType if no normalizeValue found (technically should never happen, but just in case)
		if (
			priceMarkerType === 'posneg' &&
			typeof normalizeValue !== 'number'
		) {
			priceMarkerType = markerType.LINE;
			this.core.warn('Marker type set to "posneg" but normalize values not found. Changing to "line" markerType.');
		}

		// render as either SessionBreak or NormalSession
		if (sessionType === 'SessionBreak') {
			const color = this._getMarkerLineStyle('color', sessionType);
			const width = this._getMarkerLineStyle('width', sessionType);
			const lineDash = this._getMarkerLineStyle('lineDash', sessionType);
			const dataBreak = this._prependNormalizedPoint(dataPrice);

			// the SessionBreak indicator supports less markerTypes than the NormalSession indicator (only posneg, line, lineWithShapes, fill, and stepped)
			switch (priceMarkerType) {
				case markerType.POSNEG:
					// top clip
					this._clip(ctx, 'above', normalizeValue);
					this._renderLine(
						dataBreak,
						color,
						width,
						lineDash
					);
					this._clipEnd(ctx);
					// bottom clip
					this._clip(ctx, 'below', normalizeValue);
					this._renderLine(
						dataBreak,
						color,
						width,
						lineDash
					);
					this._clipEnd(ctx);
					break;
				case markerType.STEPPED:
					this._renderSteppedLine(
						dataBreak,
						color,
						width,
						lineDash
					);
					break;
				default: // line, linewithshapes, fill
					this._renderLine(
						dataBreak,
						color,
						width,
						lineDash
					);
					break;
			}
		} else {

			const prevCloseIndicators = this.panel.core.getIndicatorsById('previousclose');
			const prevCloseIndicator = prevCloseIndicators.length ? prevCloseIndicators[0] : null;
			const prevCloseVersion = prevCloseIndicator ? prevCloseIndicator.params.inputs.find(i => i.name === 'Version').value : 1;

			// Normal Session
			switch (priceMarkerType) {
				case markerType.BAR:
					this._renderBar(
						dataPrice,
						style.bar.color
					);
					break;
				case markerType.CANDLESTICK:
					this._renderCandlestick(
						dataPrice,
						style.candlestick.positive.color,
						style.candlestick.negative.color,
						style.candlestick.neutral.color,
						style.candlestick.line
					);
					break;
				case markerType.HLC:
					this._renderHLC(
						dataPrice,
						style.ohlc.positive.color,
						style.ohlc.negative.color,
						style.ohlc.neutral.color,
						style.ohlc.line
					);
					break;
				case markerType.LINE:
					this._renderLine(
						dataPrice,
						style.color,
						style.width,
						style.lineDash
					);
					break;
				case markerType.LINEWITHSHAPES:
					ctx.save();
					ctx.beginPath();
					// Create shape with offset for clipping area, but don't draw
					ctx.rect(0, 0, this.panel.size.width, this.panel.size.height);
					this._renderShape(
						dataPrice,
						{
							...style.shape,
							beginPath: false,
							color: null,
							// color: 'black',
							fillColor: null,
							length: style.shape.length + 2 * style.shape.offset,
							lineWidth: style.shape.width
						}
					);
					ctx.clip();
					this._renderLine(
						dataPrice,
						style.color,
						style.width,
						style.lineDash
					);
					ctx.restore(); // Get rid of clipping area
					this._renderShape(
						dataPrice,
						{
							...style.shape,
							fillColor: style.shape.background.color,
							lineWidth: style.shape.width
						}
					);
					break;
				case markerType.OHLC:
					this._renderOHLC(
						dataPrice,
						style.color,
						style.ohlc.positive.color,
						style.ohlc.negative.color,
						style.ohlc.neutral.color,
						style.ohlc.line
					);
					break;
				case markerType.POSNEG:

					if (!prevCloseIndicator || prevCloseVersion === 1 || !this.core.state.isIntraday) {
						// top clip
						this._clip(ctx, 'above', normalizeValue);
						this._renderFill(
							dataPrice,
							style.posneg.positive.background.color,
							style.posneg.positive.background.colorStop,
							normalizeValue,
							'pos'
						);
						this._renderLine(
							dataPrice,
							style.posneg.positive.color,
							style.posneg.width,
							style.posneg.lineDash
						);
						this._clipEnd(ctx);
						// bottom clip
						this._clip(ctx, 'below', normalizeValue);
						this._renderFill(
							dataPrice,
							style.posneg.negative.background.color,
							style.posneg.negative.background.colorStop,
							normalizeValue,
							'neg'
						);
						this._renderLine(
							dataPrice,
							style.posneg.negative.color,
							style.posneg.width,
							style.posneg.lineDash
						);
						this._clipEnd(ctx);
					} else {
						// special case for previous close indicator version 2

						const boxes = prevCloseIndicator._getV2BoundingBoxes(data);

						boxes.forEach(box => {

							// top clip
							this._clip(ctx, 'above', box.prevClose, box.dayLeft, box.dayRight);
							this._renderFill(
								dataPrice,
								style.posneg.positive.background.color,
								style.posneg.positive.background.colorStop,
								box.prevClose,
								'pos'
							);
							this._renderLine(
								dataPrice,
								style.posneg.positive.color,
								style.posneg.width,
								style.posneg.lineDash
							);
							this._clipEnd(ctx);
							// bottom clip
							this._clip(ctx, 'below', box.prevClose, box.dayLeft, box.dayRight);
							this._renderFill(
								dataPrice,
								style.posneg.negative.background.color,
								style.posneg.negative.background.colorStop,
								box.prevClose,
								'neg'
							);
							this._renderLine(
								dataPrice,
								style.posneg.negative.color,
								style.posneg.width,
								style.posneg.lineDash
							);
							this._clipEnd(ctx);
						});
					}

					break;
				case markerType.SHAPE:
					this._renderShape(
						dataPrice,
						{
							...style.shape,
							fillColor: style.shape.background.color,
							lineWidth: style.shape.width
						}
					);
					break;
				case markerType.STEPPED:
					this._renderSteppedLine(
						dataPrice,
						style.color,
						style.width,
						style.lineDash
					);
					break;
				default:
					this._renderFill(
						dataPrice,
						style.background.color,
						style.background.colorStop
					);
					this._renderLine(
						dataPrice,
						style.color,
						style.width,
						style.lineDash
					);
					break;
			}
		}
	}

	/**
	 * Renders indicator fill
	 * @param {object} data Symbol data object
	 * @param {string} fillColor Indicator fill color
	 * @param {string} fillColorStop Indicator stop fill color
	 * @param {number} fillToValue Value to fill to
	 */
	_renderFill(data, fillColor, fillColorStop, fillToValue, posNeg) {
		data = this._prependNormalizedPoint(data);
		// Handle case where non-numeric fillToValue is passed in
		if (typeof fillToValue !== 'number' && this.panel.isNormalized) fillToValue = 0.0;

		const fillToY = subpixel.px(Math.min(this.yScale(fillToValue), this.panel.size.height));
		const y0 = (!posNeg || posNeg === 'pos') ? 0 : fillToY;
		const y1 = (!posNeg || posNeg === 'neg') ? this.panel.size.height : fillToY;
		const fillStyle = fillColorStop
			? this._getLinearGradient(
				this.panel.rootContext,
				fillColor,
				fillColorStop,
				0, y0,
				0, y1
			)
			: fillColor;

		this.markers.line.renderFill(
			this.panel.rootContext,
			data,
			this.panel.size.padding.top + this.panel.size.height,
			this.xScale,
			this.yScale,
			fillStyle,
			fillToValue
		);
	}

	/**
	 * Renders a line indicator
	 * @param {object} data Symbol data object
	 * @param {string} lineColor Indicator line color
	 * @param {number} penPxOn Dashed line settings
	 * @param {number} penPxOff Dashed line settings
	 * @return {void}
	 */
	_renderLine(data, lineColor, lineWidth, lineDash) {
		data = this._prependNormalizedPoint(data);
		return this.markers.line.render(
			this.panel.rootContext,
			data,
			this.xScale,
			this.yScale,
			lineColor,
			lineWidth,
			lineDash
		);
	}

	/**
	 * Renders a stepped line
	 * @param {object} data Symbol data object
	 * @param {string} lineColor Indicator line color
	 * @return {void}
	 */
	_renderSteppedLine(data, lineColor, lineWidth, lineDash) {
		data = this._prependNormalizedPoint(data);
		return this.markers.line.render(
			this.panel.rootContext,
			data,
			this.xScale,
			this.yScale,
			lineColor,
			lineWidth,
			lineDash,
			true
		);
	}

	/**
	 * Renders a bar line
	 * @param {object} data Symbol data object
	 * @return {void}
	 */
	_renderBar(data = [], color) {
		return this.markers.bar.render(
			this.panel,
			this.panel.rootContext,
			data,
			this.xScale,
			this.yScale,
			color
		);
	}

	/**
	 * Renders the specified shape at the points specified by the data passed in
	 * @param {Object} data - Data to be plotted on chart
	 * @param {*} args - Arguments used to configure how to render the shape
	 */
	_renderShape(
		data,
		{
			beginPath,
			color,
			fillColor,
			length,
			lineWidth,
			offset,
			type
		}
	) {
		data = this._prependNormalizedPoint(data);
		this.markers.shape[type].render(
			this.panel.rootContext,
			data,
			this.xScale,
			this.yScale,
			{
				beginPath,
				color,
				fillColor,
				length,
				lineWidth,
				offset
			}
		);
	}

	/**
	 * Renders a candlestick line
	 * @param {object} data Symbol data object
	 */
	_renderCandlestick(data, colorPos, colorNeg, colorNeutral, lineStyle) {
		this.markers.candlestick.params.fillType = this.params.candleFillType;
		this.markers.candlestick.params.fillRule = this.params.candleFillRule;
		if (this.params.style.candlestick.size.large === true) {
			lineStyle = lineStyle.large.width;
		} else {
			lineStyle = lineStyle.default.width;
		}
		this.markers.candlestick.render(
			this.panel,
			this.panel.rootContext,
			data,
			this.xScale,
			this.yScale,
			colorPos,
			colorNeg,
			colorNeutral,
			lineStyle
		);
	}

	/**
	 * Renders an OHLC line
	 * @param {object} data Symbol data object
	 */
	_renderOHLC(data, color, colorUp, colorDown, colorNeutral, lineStyle) {
		if (this.params.style.ohlc.size.large === true) {
			lineStyle = lineStyle.large.width;
		} else {
			lineStyle = lineStyle.default.width;
		}
		switch (this.params.ohlcType) {
			case ohlcType.POSNEG:
				this.markers.candlestick.renderOHLCPosNeg(
					this.panel,
					this.panel.rootContext,
					data,
					this.xScale,
					this.yScale,
					colorUp,
					colorDown,
					colorNeutral,
					'ohlc',
					lineStyle
				);
				break;
			case ohlcType.NEWHIGHS:
				this.markers.candlestick.renderOHLCNewHighs(
					this.panel,
					this.panel.rootContext,
					data,
					this.xScale,
					this.yScale,
					colorUp,
					colorNeutral,
					'ohlc',
					lineStyle
				);
				break;
			case ohlcType.NEWLOWS:
				this.markers.candlestick.renderOHLCNewLows(
					this.panel,
					this.panel.rootContext,
					data,
					this.xScale,
					this.yScale,
					colorDown,
					colorNeutral,
					'ohlc',
					lineStyle
				);
				break;
			case ohlcType.MONO:
			default: // Draw monochrome
				this.markers.candlestick.renderOHLC(
					this.panel,
					this.panel.rootContext,
					data,
					this.xScale,
					this.yScale,
					color,
					'ohlc',
					lineStyle
				);
		}
	}

	/**
	 * Renders an HLC line
	 * @param {object} data Symbol data object
	 */
	_renderHLC(data, colorUp, colorDown, colorNeutral, lineStyle) {
		if (this.params.style.ohlc.size.large === true) {
			lineStyle = lineStyle.large.width;
		} else {
			lineStyle = lineStyle.default.width;
		}
		this.markers.candlestick.renderOHLCPosNeg(
			this.panel,
			this.panel.rootContext,
			data,
			this.xScale,
			this.yScale,
			colorUp,
			colorDown,
			colorNeutral,
			'hlc',
			lineStyle
		);
	}

	/**
	 * Sets the OHLC type used for rendering OHLC markers
	 * @param {ohlcType} type - The OHLC type
	 */
	set ohlcType(type) {
		if (Object.values(ohlcType).includes(type)) {
			this.params.ohlcType = type;
		} else {
			this.core.warn("Can't set invalid OHLC type");
		}
	}

	/**
	 * Gets the OHLC type
	 * @return {ohlcType} The OHLC type
	 */
	get ohlcType() {
		return this.params.ohlcType;
	}

	/**
	 * Sets the marker type
	 * @param {markerType} type - The marker type
	 */
	set markerType(type) {
		if (Object.values(markerType).includes(type)) {
			this.params.markerType = type;
			this.core.eventEmitter.emit(eventTypes.PRICE_MARKER_TYPE_SET, { type });
		} else {
			this.core.warn("Can't set invalid marker type");
		}
	}

	get markerType() {
		return this.params.markerType;
	}
}
